// @flow
import React from 'react';
import { connect } from 'react-redux';
import styles from './PaymentSummary.css';
import { FormattedMessage } from 'react-intl';
import PlanPrice from './PlanPrice';
import { selectCurrency, selectShouldShowCurrencyNote } from '../../selectors/client';
import {
  A_MONTH_IN_DAYS,
  PLAN_ID_PPC_12M,
  PLAN_ID_PPC_1M,
  PLAN_ID_PP_1M,
  PLAN_ID_PP_12M,
  PLAN_ID_PPC_GROUP,
  PLAN_ID_PP_GROUP,
  PLAN_ID_12M_GROUP,
  SUBSCRIPTION_PLANS,
} from '../../constants';
import { planTitleMessages } from '../messages/PlansOverviewMessages';
import IconLabel from '../util/IconLabel';
import Price from '../common/Price';
import ToggleSwitch from '../util/ToggleSwitch';
import classNames from 'classnames';

type OwnProps = {
  isTrialAvailable: boolean,
  trialDurationDays: number,
  selectedPlan: string | null,
  setSelectedPlan: Function,
  relativeDiscount?: number,
  price: number,
  goBack: Function,
  reducedPriceDuration?: number,
  isStripeLoading: boolean,
};

type MapStateToProps = {
  currency: string,
  shouldShowCurrencyNote: boolean,
};

type Props = OwnProps & MapStateToProps;

const PaymentSummary = ({
  isTrialAvailable,
  trialDurationDays,
  selectedPlan,
  setSelectedPlan,
  relativeDiscount,
  price,
  goBack,
  reducedPriceDuration,
  isStripeLoading,
  currency,
  shouldShowCurrencyNote,
}: Props) => {
  const onToggleSwitchChange = e => {
    let targetPlan = selectedPlan;
    if (PLAN_ID_PPC_GROUP.includes(selectedPlan)) {
      targetPlan = e.target.checked ? PLAN_ID_PPC_12M : PLAN_ID_PPC_1M;
    } else if (PLAN_ID_PP_GROUP.includes(selectedPlan)) {
      targetPlan = e.target.checked ? PLAN_ID_PP_12M : PLAN_ID_PP_1M;
    }
    setSelectedPlan(targetPlan);
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <FormattedMessage
          id="premium.payment-summary.chosen-plan"
          defaultMessage="Your chosen plan"
        />
        {[...PLAN_ID_PPC_GROUP, ...PLAN_ID_PP_GROUP].includes(selectedPlan) && (
          <ToggleSwitch
            className={styles.toggleSwitch}
            checked={PLAN_ID_12M_GROUP.includes(selectedPlan)}
            id={selectedPlan}
            name={selectedPlan}
            onChange={onToggleSwitchChange}
            descriptionLeft={
              <FormattedMessage
                id="premium.plan-summary.selected-plan.pay-annually-with-discount"
                defaultMessage="Pay annually (-{discountPercentage}%)"
                values={{
                  discountPercentage:
                    SUBSCRIPTION_PLANS[
                      PLAN_ID_PPC_GROUP.includes(selectedPlan) ? PLAN_ID_PPC_12M : PLAN_ID_PP_12M
                    ].discountToMonthly,
                }}
              />
            }
          />
        )}
      </div>
      <div className={styles.plan}>
        <div>
          <div
            className={styles.planName}
            data-test="payment-summary.selected-plan.title"
            data-test-value={selectedPlan}
          >
            IDAGIO&nbsp;
            <FormattedMessage {...planTitleMessages[selectedPlan]} />
          </div>
          {isTrialAvailable && (
            <div className={styles.planName} data-test="payment-summary.trial.price">
              <FormattedMessage
                id="premium.payment-summary.free-trial"
                defaultMessage="{price} for {trialDurationDays} days"
                values={{
                  price: <Price price={0} />,
                  trialDurationDays,
                }}
              />
            </div>
          )}
          <div
            className={classNames(styles.actualPrice, {
              [styles.actualPriceDimmed]: isTrialAvailable,
            })}
          >
            {isTrialAvailable && (
              <React.Fragment>
                <FormattedMessage id="premium.payment-summary.then" defaultMessage="then" />
                &nbsp;
              </React.Fragment>
            )}
            <PlanPrice
              price={price}
              relativeDiscount={relativeDiscount}
              interval={PLAN_ID_12M_GROUP.includes(selectedPlan) ? 'yearly' : 'monthly'}
              className={classNames(styles.price, {
                [styles.priceDimmed]: isTrialAvailable,
              })}
            />
            &nbsp;
            {PLAN_ID_12M_GROUP.includes(selectedPlan) ? (
              <FormattedMessage
                id="premium.payment-summary.billed-annually"
                defaultMessage="(billed annually)"
              />
            ) : (
              <FormattedMessage
                id="premium.payment-summary.billed-monthly"
                defaultMessage="(billed monthly)"
              />
            )}
            <br />
            {reducedPriceDuration && (
              <span
                data-test="payment-summary.reduced-price-duration"
                data-test-value={Math.floor(reducedPriceDuration / A_MONTH_IN_DAYS)}
              >
                <FormattedMessage
                  id="premium.payment-summary.reduced-price-first-duration"
                  defaultMessage="discount applies for the first {months} months"
                  values={{ months: Math.floor(reducedPriceDuration / A_MONTH_IN_DAYS) }}
                />
              </span>
            )}
            {shouldShowCurrencyNote && (
              <span className={styles.currencyNote} data-test="payment-summary.currency-note">
                <FormattedMessage
                  id="premium.payment-summary.currency-conversion-note"
                  defaultMessage="Billed in {currency} — your bank may apply a conversion at its own rate."
                  values={{ currency }}
                />
              </span>
            )}
          </div>
        </div>
        {!isStripeLoading && (
          <button
            onClick={goBack}
            className={styles.changeButton}
            data-test="payment-summary.btn.change"
          >
            <FormattedMessage id="premium.payment-summary.btn.change" defaultMessage="Change" />
          </button>
        )}
      </div>
      <div className={styles.stripeInfo}>
        <div className={styles.checkout}>
          <IconLabel name="lock" className={styles.lock} />
          <span className={styles.text}>
            <FormattedMessage
              id="premium.payment-summary.stripe-info.checkout"
              defaultMessage="Guaranteed safe & secure checkout"
            />
          </span>
          <IconLabel name="powered-by-stripe" className={styles.poweredByStripe} />
          <span className="sr-only">Powered by Stripe</span>
        </div>
        <figure
          className={styles.iconCards}
          aria-label="Visa, Mastercard, American Express, JCB, Discover, Diners Club, Union Pay"
        />
      </div>
    </div>
  );
};

function mapStateToProps(state: Object): MapStateToProps {
  return {
    currency: selectCurrency(state),
    shouldShowCurrencyNote: selectShouldShowCurrencyNote(state),
  };
}

export default connect(mapStateToProps)(PaymentSummary);
